/**
 * The framework library to develop Enterprise Level Business Applications
 *
 * @remarks
 *
 * aeppic (pronounced /appic/ as the ae really comes from the German ä) is
 * a <b>business application platform</b> with a strong structure of ensuring
 * applications can be developed quickly and then last for multiple decades.
 *
 * How this is done from an architectural point as well as from an
 * implementation side is better explained in the <a href="../../..">Overview</a>
 * instead of reading this reference documentation.
 *
 * @packageDocumentation
 *
 * @privateRemarks
 *
 * The Overview href links back to the main documentation entry point which
 * will always be two levels up (from <root>/code/aeppic/aeppic.html -> <root>/)
 *
 */

export { default as default } from './aeppic/index.js'

import * as Types from '@aeppic/types'
export { Types }

export { default as uuid } from '@aeppic/shared/uuid'
export { EventEmitter } from '@aeppic/shared/event-emitter'
export { BrowserHistoryNavigation } from './aeppic/browser-history-navigation'

export * from './aeppic/index.js'

export {  ChangeType } from './model/change-tracker.js'
export type { Change, ChangeSet } from './model/change-tracker.js'

export { default as Preferences } from './aeppic/utils/preferences.js'
export { default as Services, SERVICE_FORM_ID, SERVICE_DEFINITION_FORM_ID, GLOBAL_SERVICES_FOLDER_ID } from './aeppic/utils/services.js'
export { Designs, DesignSelection, DesignLookupSelection } from './aeppic/dynamic/design-lookup.js'
export { Forms } from './aeppic/forms/forms.js'
export { Controls } from './aeppic/controls.js'
export type { DesignLookupResult, DesignLookupSource, DesignSelector, DesignSelectorInput } from './aeppic/dynamic/design-lookup.js'
export { default as Translator } from './aeppic/utils/translator.js'
export { Actions } from './aeppic/actions.js'
export type { ActionPackageLoader } from './aeppic/actions.js'
export { wrapInCachedQueryInterface } from './aeppic/cached-query-api.js'
export { createQueryToolbarRuntime } from './aeppic/query-toolbar-runtime/index.js'
export type { QueryToolbarAeppicApi, QueryToolbarRuntimeOptions } from './aeppic/query-toolbar-runtime/index.js'
export { default as Model, importIntoModel, Importer, EditableDocument, Form, FieldFormatter, Formatting, Indexing, getDocumentId, toQueryAndOptions, Feature as ModelFeature, sort, enableProxyMode, disableProxyMode, Query, getPreviousChanges, PreviousChange, PreviousFieldChange, PreviousFormChange, PreviousListChange, PreviousValueChange, validateDocumentAgainstForm, validateDocumentDataAgainstForm, validateDocumentDataAgainstFormDown } from './model/index.js'
export type { ImportResult, Write, FindOptions, FindOneOptions, ModelQueryExecutionResult, SaveOptions, NewDocumentOptions, FilterFunction, LimitedAccessOptions, StampOptions, UndeleteOptions, GetOptions, ChangeDocumentFormOptions, DescendantTimestampTracking, ImportVersionOptions, ModelOptions, TypeValidationOptions, DocumentImportResult, IDocumentWatcher, DocumentWatchCallback, TextFormatter, TextFormatterContext, DocumentValidationIssue, DocumentValidationIssueKind, DocumentValidationResult, ValidateDocumentDataOptions } from './model/index.js'
export type { AeppicInterface, AeppicUserInterface, AeppicUiInterface } from './aeppic/aeppic.js'
export type { Logger } from './model/log.js'
export type { DocumentFilter } from './model/filter.js'
export { Features } from './aeppic/features.js'
export type { FeatureNamespace } from './aeppic/features.js'
export { Warn } from './aeppic/warn.js'
export { LookupTrace } from './aeppic/lookup/form-oriented-lookup.js'
export { DesignLookup, DesignMatchStrategy } from './aeppic/lookup/design-lookup.js'
export { ControlLookup, ControlMatchStrategy } from './aeppic/lookup/control-lookup.js'

import * as Querying from './model/query/index.js'
export { Querying }

export { isDocument, isReference } from './model/is.js'
export { resolveDocumentPath } from './model/resolve-path.js'
export { default as resolveField } from './model/query/resolve-field.js'

export type { CommandAvailability, CommandCheck, DocumentCommands } from './aeppic/commands/availability.js'
export type { ConditionEvaluation, ConditionOptions } from './aeppic/commands/conditions.js'
export { Commands } from './aeppic/commands/index.js'
export type { ExecuteOptions as CommandExecuteOptions } from './aeppic/commands/index.js'
export { Navigation } from './aeppic/navigation.js'
export { NavigationLookup, navigationForms } from './aeppic/lookup/navigation-lookup.js'
export type { NavigationLookupResult, NavigationLookupApplication, NavigationLookupEntry, NavigationLookupChildren } from './aeppic/lookup/navigation-lookup.js'
export type { NavigationTarget, NavigationWatchCallback } from './aeppic/navigation.js'
export { Validations, ValidationResultBuilder, VALIDATION_CATEGORIES } from './aeppic/validations.js'
export type { BasicValidationResult, FieldValidationResult, TargetValidationResult, ValidationCategory, ValidationContext, ValidationEvidence, ValidationMessage, ValidationResult, ValidationRunOptions, ValidationSetResult, ValidationStatus } from './aeppic/validations.js'
export { default as Sortable } from './aeppic/modules/sortable.js'
export type { SortableOptions, SortableEvent, SortableInstance, ManagedSortableInstance, SortableOwner, ReorderResult } from './aeppic/modules/sortable.js'
export { default as Zip } from './aeppic/modules/zip.js'
export type { ZipEntry, ZipListOptions, ZipPreviewKind, ZipReadEntryOptions } from './aeppic/modules/zip.js'

// Re-Export modules incorporated into build to ensure users can use the exact same versions.
// Modules are exported in the export 'modules' and are named exactly like their package names.
import * as luxon from 'luxon'
import Vue from './externals/vue.js'
import animejs from 'animejs'
import FileSaver from 'file-saver'
import Sortable from './aeppic/modules/sortable.js'
import Zip from './aeppic/modules/zip.js'

import Popup from './aeppic/popup.js'

const modules = {
  luxon,
  vue: Vue,
  Vue,
  animejs,
  'file-saver': FileSaver,
  Popup,
  Sortable,
  zip: Zip,
}

export { modules }

export type { SupportApi, SupportState, SupportStartOptions, SupportRecordingInfo, SupportReport, SupportReportOptions, SupportPlayback, SupportAnnotation, SupportErrorCode } from './aeppic/support/types.js'
export { SupportError } from './aeppic/support/types.js'

export { Toasts } from './aeppic/toasts.js'
export type { Toast, ToastOptions, ToastUpdate, ToastCompletion, ToastKind, ToastRenderer } from './aeppic/toasts.js'

export { LocalModel } from './aeppic/local-model.js'
export type { LocalModelApi, LocalModelError, LocalModelPhase, LocalModelRoute, LocalModelState } from './aeppic/local-model.js'

export { normalizeControlLookupVariant, transformControlSelector, controlNameForPlaceholder, controlPreloadIdentity } from './aeppic/forms/preload-controls.js'
export type { ControlLookupVariant } from './aeppic/forms/preload-controls.js'

export type { DocumentDialogOptions, DocumentDialogLayouts, DocumentDialogLayoutContext, DocumentDialogCommand, DocumentDialogAction, DocumentDialogResult } from './aeppic/aeppic.js'
